import { createContext, useContext, useMemo } from 'react'
import { toast } from 'sonner'

export const ToastContext = createContext<boolean>(false)

type ToastFn = (title: string, description?: string) => void
type RetryToastFn = (title: string, description: string | undefined, onRetry: () => void) => void
type ActionToastFn = (
  title: string,
  description: string | undefined,
  actionLabel: string,
  onAction: () => void,
) => void

export type UseToastResult = {
  success: ToastFn
  error: ToastFn
  info: ToastFn
  warning: ToastFn
  errorWithRetry: RetryToastFn
  successWithAction: ActionToastFn
}

export function useToast(): UseToastResult {
  const mounted = useContext(ToastContext)
  if (!mounted) throw new Error('useToast must be used within a ToastProvider')
  return useMemo<UseToastResult>(() => ({
    success: (title, description) => toast.success(title, { description }),
    error: (title, description) => toast.error(title, { description }),
    info: (title, description) => toast.info(title, { description }),
    warning: (title, description) => toast.warning(title, { description }),
    errorWithRetry: (title, description, onRetry) =>
      toast.error(title, { description, action: { label: 'Retry', onClick: onRetry } }),
    successWithAction: (title, description, actionLabel, onAction) =>
      toast.success(title, { description, action: { label: actionLabel, onClick: onAction } }),
  }), [])
}
